import React, { useState, useEffect } from 'react';
import { openPrintWindow } from '../../utils/printDocument';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Collapse,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';

const TimeTableView = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});

  // Charger la liste du personnel avec leurs emplois du temps
  useEffect(() => {
    const fetchPersonnel = async () => {
      try {
        setLoading(true);
        const personnelQuery = query(
          collection(db, 'users'),
          where('chefServiceId', '==', currentUser.uid),
          where('role', '==', 'personnel')
        );
        const querySnapshot = await getDocs(personnelQuery);
        const personnelData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPersonnel(personnelData);
      } catch (error) {
        console.error('Erreur lors du chargement du personnel:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchPersonnel();
    }
  }, [currentUser]);

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString;
  };

  const handleRowExpand = (personnelId) => {
    setExpandedRows(prev => ({
      ...prev,
      [personnelId]: !prev[personnelId]
    }));
  };

  const formatDaySchedulePrint = (schedule) => {
    if (!schedule) return { label: 'Non défini', isCoupure: false };
    if (schedule.withBreak) {
      const hasMorning = schedule.morning?.start && schedule.morning?.end;
      const hasAfternoon = schedule.afternoon?.start && schedule.afternoon?.end;
      if (hasMorning && hasAfternoon) {
        return {
          label: `${schedule.morning.start} - ${schedule.morning.end} &nbsp;&nbsp;|&nbsp;&nbsp; Reprise&nbsp;: ${schedule.afternoon.start} - ${schedule.afternoon.end}`,
          isCoupure: true,
        };
      }
      if (hasMorning) return { label: `${schedule.morning.start} - ${schedule.morning.end}`, isCoupure: true };
      if (hasAfternoon) return { label: `Reprise&nbsp;: ${schedule.afternoon.start} - ${schedule.afternoon.end}`, isCoupure: true };
    }
    if (schedule.continuous?.start && schedule.continuous?.end) {
      return { label: `${schedule.continuous.start} - ${schedule.continuous.end}`, isCoupure: false };
    }
    return { label: 'Horaires non définis', isCoupure: false };
  };

  const handlePrint = (person) => {
    // Calculer le total des heures hebdomadaires
    const totalMinutes = Object.values(person.schedule || {}).reduce((total, day) => {
      if (!day.active) return total;
      let dayTotal = 0;
      if (day.withBreak) {
        if (day.morning?.start && day.morning?.end) {
          const [startHour, startMin] = day.morning.start.split(':').map(Number);
          const [endHour, endMin] = day.morning.end.split(':').map(Number);
          dayTotal += (endHour * 60 + endMin) - (startHour * 60 + startMin);
        }
        if (day.afternoon?.start && day.afternoon?.end) {
          const [startHour, startMin] = day.afternoon.start.split(':').map(Number);
          const [endHour, endMin] = day.afternoon.end.split(':').map(Number);
          dayTotal += (endHour * 60 + endMin) - (startHour * 60 + startMin);
        }
      } else if (day.continuous?.start && day.continuous?.end) {
        const [startHour, startMin] = day.continuous.start.split(':').map(Number);
        const [endHour, endMin] = day.continuous.end.split(':').map(Number);
        dayTotal += (endHour * 60 + endMin) - (startHour * 60 + startMin);
      }
      return total + dayTotal;
    }, 0);

    const totalHoursFormatted = Math.floor(totalMinutes / 60) + 'h' + (totalMinutes % 60 > 0 ? String(totalMinutes % 60).padStart(2, '0') : '');

    const daysOrder = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    const activeDays = Object.entries(person.schedule || {})
      .filter(([_, s]) => s.active)
      .sort(([a], [b]) => daysOrder.indexOf(a) - daysOrder.indexOf(b));

    const tableRows = activeDays.map(([day, schedule]) => {
      const { label, isCoupure } = formatDaySchedulePrint(schedule);
      const rowStyle = isCoupure ? 'background:#fff8e1;' : '';
      const coupureBadge = isCoupure ? `<span class="badge-coupure">COUPURE</span>` : '';
      const tasks = (schedule.tasks || []).map(t => `<li>${t}</li>`).join('');
      return `
        <tr style="${rowStyle}">
          <td class="day-cell">${day}</td>
          <td class="horaire-cell">${label}${coupureBadge}</td>
          <td class="tasks-cell">${tasks ? `<ul>${tasks}</ul>` : '<span style="color:#aaa;">—</span>'}</td>
        </tr>`;
    }).join('');

    const extraCss = `
            .print-page { font-size: 10.5px; }

            /* ── En-tête ── */
            .header-bar {
              background: #0f4c5c;
              color: #fff;
              padding: 9px 14px;
              border-radius: 6px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 8px;
              flex-shrink: 0;
            }
            .header-bar .title { font-size: 13px; font-weight: 700; }
            .header-bar .name  { font-size: 11px; opacity: 0.85; margin-top: 2px; }
            .header-bar .badge {
              background: #f59e0b;
              color: #fff;
              padding: 3px 11px;
              border-radius: 20px;
              font-size: 9px;
              font-weight: 700;
              white-space: nowrap;
            }

            /* ── Méta ── */
            .meta {
              display: flex;
              gap: 20px;
              margin-bottom: 8px;
              font-size: 9.5px;
              padding: 5px 10px;
              background: #f0f4f8;
              border-radius: 4px;
              flex-shrink: 0;
            }
            .meta span { color: #4a5568; }
            .meta strong { color: #0f4c5c; }

            /* ── Tableau ── */
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 8px;
              flex-shrink: 0;
            }
            thead th {
              background: #0f4c5c;
              color: #fff;
              padding: 5px 10px;
              text-align: left;
              font-size: 9px;
              letter-spacing: 0.06em;
              text-transform: uppercase;
            }
            tbody td {
              padding: 5px 10px;
              border-bottom: 1px solid #e8edf2;
              vertical-align: middle;
              font-size: 10.5px;
              line-height: 1.35;
            }
            tbody tr:last-child td { border-bottom: none; }
            .badge-coupure {
              display: inline-block;
              margin-left: 6px;
              padding: 1px 5px;
              background: #f59e0b;
              color: #fff;
              border-radius: 3px;
              font-size: 8px;
              font-weight: 700;
              vertical-align: middle;
            }
            .day-cell  { font-weight: 700; width: 11%; }
            .horaire-cell { width: 50%; }
            .tasks-cell ul { padding-left: 13px; }
            .tasks-cell li { margin: 1px 0; font-size: 10px; }

            /* ── Espace flexible ── */
            .spacer { flex: 1; }

            /* ── Pied de page ── */
            .footer {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-top: 7px;
              border-top: 1.5px solid #0f4c5c;
              font-size: 9px;
              color: #4a5568;
              flex-shrink: 0;
            }
            .total-box {
              background: #0f4c5c;
              color: #fff;
              padding: 3px 12px;
              border-radius: 4px;
              font-weight: 700;
              font-size: 11px;
            }
    `;

    const bodyHtml = `
          <div class="print-page">
            <div class="header-bar">
              <div>
                <div class="title">Emploi du temps hebdomadaire</div>
                <div class="name">${person.firstName} ${person.lastName}</div>
              </div>
              <div class="badge">EduGestion 2026</div>
            </div>

            <div class="meta">
              <div><span>Département&nbsp;: </span><strong>${person.department || '—'}</strong></div>
              <div><span>Poste&nbsp;: </span><strong>${person.position || '—'}</strong></div>
              <div><span>Imprimé le&nbsp;: </span><strong>${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</strong></div>
            </div>

            <table>
              <thead>
                <tr>
                  <th class="day-cell">Jour</th>
                  <th class="horaire-cell">Horaire</th>
                  <th>Tâches</th>
                </tr>
              </thead>
              <tbody>${tableRows}</tbody>
            </table>

            <div class="spacer"></div>

            <div class="footer">
              <span>Système de gestion scolaire — EduGestion 2026</span>
              <div class="total-box">Total semaine&nbsp;: ${totalHoursFormatted}</div>
            </div>
          </div>`;

    openPrintWindow(`Emploi du temps — ${person.firstName} ${person.lastName}`, bodyHtml, extraCss, 'height=700,width=900');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/chef-service/dashboard')}
          sx={{ mb: 2 }}
        >
          Retour au tableau de bord
        </Button>
        <Typography variant="h5" component="h1">
          Emplois du temps du Personnel
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Nom</TableCell>
              <TableCell>Prénom</TableCell>
              <TableCell>Département</TableCell>
              <TableCell>Heures/Semaine</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {personnel.map((person) => (
              <React.Fragment key={person.id}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleRowExpand(person.id)}
                    >
                      {expandedRows[person.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{person.lastName}</TableCell>
                  <TableCell>{person.firstName}</TableCell>
                  <TableCell>{person.department}</TableCell>
                  <TableCell>
                    {Object.values(person.schedule || {}).reduce((total, day) => {
                      if (!day.active) return total;
                      let dayTotal = 0;
                      if (day.withBreak) {
                        if (day.morning?.start && day.morning?.end) {
                          const [startHour, startMin] = day.morning.start.split(':').map(Number);
                          const [endHour, endMin] = day.morning.end.split(':').map(Number);
                          dayTotal += (endHour * 60 + endMin) - (startHour * 60 + startMin);
                        }
                        if (day.afternoon?.start && day.afternoon?.end) {
                          const [startHour, startMin] = day.afternoon.start.split(':').map(Number);
                          const [endHour, endMin] = day.afternoon.end.split(':').map(Number);
                          dayTotal += (endHour * 60 + endMin) - (startHour * 60 + startMin);
                        }
                      } else if (day.continuous?.start && day.continuous?.end) {
                        const [startHour, startMin] = day.continuous.start.split(':').map(Number);
                        const [endHour, endMin] = day.continuous.end.split(':').map(Number);
                        dayTotal += (endHour * 60 + endMin) - (startHour * 60 + startMin);
                      }
                      return total + dayTotal;
                    }, 0) / 60}h
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handlePrint(person)}
                    >
                      <PrintIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={expandedRows[person.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Typography variant="h6" gutterBottom component="div">
                          Emploi du temps détaillé
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Jour</TableCell>
                              <TableCell>Horaire</TableCell>
                              <TableCell>Tâches</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(person.schedule || {}).filter(([_, schedule]) => schedule.active).sort(([dayA], [dayB]) => {
                              const daysOrder = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
                              return daysOrder.indexOf(dayA) - daysOrder.indexOf(dayB);
                            }).map(([day, schedule]) => {
                              const { label: schedLabel, isCoupure } = formatDaySchedulePrint(schedule);
                              // label uses HTML entities — strip for JSX display
                              const displayLabel = schedLabel
                                .replace(/&nbsp;/g, '\u00a0')
                                .replace(/&amp;/g, '&');
                              return (
                              <TableRow key={day} sx={{ background: isCoupure ? '#fff8e1' : 'inherit' }}>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                                  {day}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{displayLabel}</span>
                                    {isCoupure && (
                                      <Chip
                                        label="Coupure"
                                        size="small"
                                        sx={{ background: '#f59e0b', color: '#fff', fontWeight: 700, fontSize: '10px', height: 20 }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    {schedule.tasks?.map((task, index) => (
                                      <li key={index}>{task}</li>
                                    ))}
                                  </ul>
                                </TableCell>
                              </TableRow>
                            );})}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default TimeTableView;
